'use client';

import React from 'react';
import ArticleCard from '@/components/kb/ArticleCard';
import Link from 'next/link';
import { ArrowRight, TrendingUp } from 'lucide-react';
import { useArticles, Article } from '@/context/ArticlesContext';

function MostViewedLink({ item, index, getCategoryName }: { item: Article; index: number; getCategoryName: (id: string) => string }) {
  const { setSelectedArticleId } = useArticles();
  return (
    <Link
      href="/article-material-detail-page"
      className="flex items-start gap-3 p-3 rounded-lg hover:bg-surface-2 transition-colors group"
      onClick={() => setSelectedArticleId(item.id)}
    >
      <span className="text-xl font-700 tabular-nums text-gold/30 group-hover:text-gold/60 transition-colors w-6 flex-shrink-0 text-center leading-tight">
        {index + 1}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground group-hover:text-gold transition-colors line-clamp-2 leading-snug">
          {item.title}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-muted-foreground">{getCategoryName(item.category)}</span>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground tabular-nums">
            <TrendingUp className="w-3 h-3" />
            {item.viewCount?.toLocaleString()}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function RecentArticles() {
  const { getRecentlyUpdated, getMostViewed, categories } = useArticles();

  const recentArticles = getRecentlyUpdated(5);
  const mostViewedArticles = getMostViewed(5);

  function getCategoryName(categoryId: string) {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  }

  function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <section>
      <div className="grid grid-cols-1 xl:grid-cols-4 2xl:grid-cols-4 gap-8">
        {/* Recently updated */}
        <div className="xl:col-span-3">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="section-label mb-1">Latest Updates</p>
              <h2 className="font-serif text-2xl font-semibold text-foreground">Recently Updated</h2>
            </div>
            <Link
              href="/section-article-list-page"
              className="hidden sm:flex items-center gap-1.5 text-sm text-gold hover:text-gold-light transition-colors font-medium"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
            {recentArticles.map(article => (
              <ArticleCard
                key={article.id}
                id={article.id}
                title={article.title}
                excerpt={article.content.replace(/#{1,3} /g, '').replace(/\*\*/g, '').slice(0, 200) + '...'}
                tags={article.tags}
                section={getCategoryName(article.category)}
                lastUpdated={formatDate(article.lastUpdated)}
                viewCount={article.viewCount}
                pinned={false}
                href="/article-material-detail-page"
              />
            ))}
            {recentArticles.length === 0 && (
              <div className="col-span-full text-center py-12 card-luxury-static rounded-xl">
                <p className="text-muted-foreground text-sm">No published articles yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* Most viewed sidebar */}
        <div className="xl:col-span-1">
          <div className="mb-6">
            <p className="section-label mb-1">Trending</p>
            <h2 className="font-serif text-2xl font-semibold text-foreground">Most Viewed</h2>
          </div>

          <div className="card-luxury-static p-4 space-y-1">
            {mostViewedArticles.map((item, index) => (
              <MostViewedLink key={item.id} item={item} index={index} getCategoryName={getCategoryName} />
            ))}
            {mostViewedArticles.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">No articles yet.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}